import classNames from 'classnames';
import * as React from 'react';
import * as ReactDOM from 'react-dom';

import Button from '../button';
import useEscape from '../hooks/use-escape';
import { useIsMounted } from '../hooks/use-is-mounted';
import Close from '../icons/18/close';
import Text from '../text';

import { ActionTypes, type Modal as Props, backgrounds } from './modal.types';
import TabTrapper from './tab-trapper';
import LinkButton from '../link-button';

const Modal: React.FC<React.PropsWithChildren<Props>> & {
  backgrounds: typeof backgrounds;
} = ({
  ariaLabel,
  background = backgrounds.primary,
  children,
  closeLabel,
  explanation,
  id,
  onClose = () => {},
  title,
  actions,
  onClick = () => {},
}) => {
  const modal = React.useRef<HTMLDivElement>(null);
  const isMounted = useIsMounted();
  useEscape(onClose);

  if (!isMounted) {
    return null;
  }

  const handleModalClick: React.MouseEventHandler = e => {
    if (modal.current && modal.current === e.target) {
      onClose();
    }
  };

  return ReactDOM.createPortal(
    <div
      aria-label={ariaLabel}
      className="modal"
      id={id}
      onClick={handleModalClick}
      ref={modal}
      role="dialog"
    >
      <div
        className={classNames('modal__content', {
          [`modal__content--${background}`]: background,
        })}
      >
        <TabTrapper isActive={true} className="modal__content-inner">
          <div
            className={classNames('modal__close-action', {
              'modal__close-action--always-shown': actions && actions.length,
            })}
          >
            <Button
              leadingIcon={<Close />}
              onClick={onClose}
              size={Button.sizes.small}
              text={closeLabel}
              variant={Button.variants.secondary}
              iconOnly
            />
          </div>
          {title || explanation ? (
            <div className="modal__nav">
              <div className="modal__texts">
                {title ? (
                  <Text
                    className="modal__title"
                    size={Text.sizes.headline2}
                    theme={Text.themes.emphasis}
                  >
                    {title}
                  </Text>
                ) : null}
                {explanation ? (
                  <div
                    className={classNames('modal__explanation', {
                      [`modal__explanation--${background}`]: background,
                    })}
                  >
                    <Text size={Text.sizes.small}>{explanation}</Text>
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
          <div className="modal__children">
            <div>{children}</div>
          </div>
          {actions && actions.length ? (
            <div className="modal__actions">
              {actions.map(action =>
                action.type === ActionTypes.LinkButton ? (
                  <LinkButton {...action.action} />
                ) : action.type === ActionTypes.Button ? (
                  <Button
                    {...action.action}
                    onClick={e => onClick(action.buttonId, e)}
                  />
                ) : null,
              )}
            </div>
          ) : (
            <div className="modal__close-action-mobile">
              <Button
                leadingIcon={<Close />}
                onClick={onClose}
                text={closeLabel}
                variant={Button.variants.secondary}
              />
            </div>
          )}
        </TabTrapper>
      </div>
    </div>,
    document.body,
  );
};

Modal.backgrounds = backgrounds;

export default Modal;
